export type LogoutReason =
  | 'manual'
  | 'session_replaced'
  | 'session_renew_required'
  | 'unauthorized';

const LOGOUT_REASON_KEY = '@groud:logout-reason';
const LOGOUT_EVENT_KEY = '@groud:auth-sync';
const LOGOUT_CHANNEL_NAME = 'groud-auth';

type LogoutSyncPayload = {
  reason: LogoutReason;
  emittedAt: number;
  id: string;
};

function isBrowser() {
  return typeof window !== 'undefined';
}

function createPayload(reason: LogoutReason): LogoutSyncPayload {
  return {
    reason,
    emittedAt: Date.now(),
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  };
}

export function persistLogoutReason(reason?: LogoutReason) {
  if (!isBrowser()) {
    return;
  }

  if (!reason || reason === 'manual') {
    window.sessionStorage.removeItem(LOGOUT_REASON_KEY);
    return;
  }

  window.sessionStorage.setItem(LOGOUT_REASON_KEY, reason);
}

export function consumeLogoutReason(): LogoutReason | undefined {
  if (!isBrowser()) {
    return undefined;
  }

  const value = window.sessionStorage.getItem(LOGOUT_REASON_KEY) as LogoutReason | null;
  window.sessionStorage.removeItem(LOGOUT_REASON_KEY);
  return value || undefined;
}

export function getLogoutReasonMessage(reason?: LogoutReason): string {
  switch (reason) {
    case 'session_replaced':
      return 'Sua conta foi acessada em outro navegador ou computador.';
    case 'session_renew_required':
      return 'Sua sessão foi invalidada e precisa de um novo login.';
    case 'unauthorized':
      return 'Sua sessão expirou. Faça login novamente.';
    default:
      return '';
  }
}

export function broadcastLogout(reason: LogoutReason) {
  if (!isBrowser()) {
    return;
  }

  const payload = createPayload(reason);
  window.localStorage.setItem(LOGOUT_EVENT_KEY, JSON.stringify(payload));

  if ('BroadcastChannel' in window) {
    const channel = new BroadcastChannel(LOGOUT_CHANNEL_NAME);
    channel.postMessage(payload);
    channel.close();
  }
}

export function subscribeToLogout(
  onLogout: (reason: LogoutReason) => void,
): () => void {
  if (!isBrowser()) {
    return () => undefined;
  }

  const handlePayload = (payload: LogoutSyncPayload | null | undefined) => {
    if (!payload?.reason) {
      return;
    }

    onLogout(payload.reason);
  };

  const handleStorage = (event: StorageEvent) => {
    if (event.key !== LOGOUT_EVENT_KEY || !event.newValue) {
      return;
    }

    try {
      handlePayload(JSON.parse(event.newValue) as LogoutSyncPayload);
    } catch {
      // Ignora eventos malformados.
    }
  };

  window.addEventListener('storage', handleStorage);

  let channel: BroadcastChannel | undefined;
  if ('BroadcastChannel' in window) {
    channel = new BroadcastChannel(LOGOUT_CHANNEL_NAME);
    channel.addEventListener('message', (event: MessageEvent<LogoutSyncPayload>) => {
      handlePayload(event.data);
    });
  }

  return () => {
    window.removeEventListener('storage', handleStorage);
    channel?.close();
  };
}
